import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

export interface EmailInputDialogData {
  title: string;
  message: string;
}

export interface EmailInputDialogResult {
  email: string;
  name: string;
}

@Component({
  selector: 'app-email-input-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <p>{{ data.message }}</p>
      <form [formGroup]="emailForm">
        <div class="name-row">
          <mat-form-field appearance="outline">
            <mat-label>First Name</mat-label>
            <input
              matInput
              type="text"
              formControlName="firstName"
              placeholder=""
              autocomplete="given-name">
            @if (emailForm.get('firstName')?.hasError('required')) {
              <mat-error>First name is required</mat-error>
            }
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Last Name</mat-label>
            <input
              matInput
              type="text"
              formControlName="lastName"
              placeholder=""
              autocomplete="family-name">
            @if (emailForm.get('lastName')?.hasError('required')) {
              <mat-error>Last name is required</mat-error>
            }
          </mat-form-field>
        </div>
        <mat-form-field appearance="outline" class="full-width" style="margin-top: 8px;">
          <mat-label>Email Address</mat-label>
          <input
            matInput
            type="email"
            formControlName="email"
            placeholder=""
            autocomplete="email">
          @if (emailForm.get('email')?.hasError('required')) {
            <mat-error>Email is required</mat-error>
          }
          @if (emailForm.get('email')?.hasError('email')) {
            <mat-error>Please enter a valid email address</mat-error>
          }
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button
        mat-raised-button
        color="primary"
        (click)="onConfirm()"
        [disabled]="emailForm.invalid">
        Send
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .name-row {
      display: flex;
      gap: 12px;
      margin-top: 16px;
    }

    .name-row mat-form-field {
      flex: 1;
    }

    mat-dialog-content {
      min-width: 400px;
    }
  `]
})
export class EmailInputDialogComponent {
  emailForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EmailInputDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EmailInputDialogData
  ) {
    this.emailForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]]
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onConfirm(): void {
    if (this.emailForm.valid) {
      this.dialogRef.close({
        email: this.emailForm.value.email,
        name: `${this.emailForm.value.firstName} ${this.emailForm.value.lastName}`.trim(),
      } as EmailInputDialogResult);
    }
  }
}
